import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';
import { FC, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { SessionUserI } from '~src/interfaces/session.types';
import { getUserFullName } from '~src/interfaces/user.interface';
import { FiltersContext } from '~src/modules/okrs/objectivesOverview/ObjectivesOverview';
import { StoreI } from '~src/rootReducer';
import AvatarBox, { AvatarType } from '~src/shared/avatarBox/AvatarBox';
import SimpleSearchInput from '~src/shared/form/inputs/SimpleSearchInput';
import DateRangeSelector from '~src/shared/selectors/dateRangeSelector/DateRangeSelector';
import RangeSelector from '~src/shared/selectors/rangeSelector/RangeSelector';
import SvgIcon from '~src/shared/SvgIcon';
import { MSFilterTypes, ObjectivesQueryI, RelationTypes, SSFilterTypes, } from '~src/store/okrs/interfaces';

import {
  CollapsibleContainer,
  CoreFiltersBlock,
  FilterBtnTxt,
  FiltersContainer,
  FiltersSetContainer,
  MyObjectivesBtn,
  ResetFiltersBtn,
  SearchWrapper,
  SelectorCol,
  ToggleButton,
} from './filtersSection.sc';
import LabelsSelector from './LabelsSelector';
import OwnersSelector from './OwnersSelector';
import StatusSelector from './StatusSelector';

export type MSFilterPropsI = Pick<ObjectivesQueryI, MSFilterTypes>;

interface StateProps {
  sessionUser: SessionUserI | null;
}

const FiltersSection: FC<StateProps> = (props) => {
  const [t] = useTranslation();
  const filtersContext = useContext(FiltersContext);
  const [isSessionUserObj, setSessionUserObj] = useState(() => {
    if (props.sessionUser) {
      const filterOwner = filtersContext.filterObj[MSFilterTypes.Owner] || [];
      return filterOwner.indexOf(props.sessionUser.uuid) !== -1;
    }
    return false;
  });

  const isSubObjectiveRelationsFilter = useMemo(() => {
    const relationsFilters = filtersContext.filterObj[MSFilterTypes.HasRelations] || [];
    return relationsFilters.indexOf(RelationTypes.SubObjectives) !== -1;
  }, [filtersContext.filterObj]);

  const dueDatesInitFilter = useMemo<[moment.Moment, moment.Moment] | undefined>(() => {
    const { filterObj } = filtersContext;
    const from = filterObj[SSFilterTypes.DueDateFrom];
    const to = filterObj[SSFilterTypes.DueDateTo];

    if (from) {
      return [moment(from), moment(to)];
    }
    return undefined;
  }, [filtersContext]);

  const progressRangeInitFilter = useMemo<[number, number]>(() => {
    const { filterObj } = filtersContext;
    if (filterObj[SSFilterTypes.ProgressTo]) {
      return [filterObj[SSFilterTypes.ProgressFrom], filterObj[SSFilterTypes.ProgressTo]] as [number, number];
    }
    return [0, 0];
  }, [filtersContext.filterObj[SSFilterTypes.ProgressTo]]);

  const getObjectivesByName = (val: string) => {
    return filtersContext.filterObjectives({
      ...filtersContext.filterObj,
      page: 1,
      [SSFilterTypes.Name]: val,
    });
  };

  const toggleSessionUserObjectives = () => {
    const filterObj: Pick<ObjectivesQueryI, 'page' | MSFilterTypes.Owner> = {
      page: 1,
      [MSFilterTypes.Owner]: undefined,
    };
    let filterOwner: string[] | undefined;

    if (filtersContext.filterObj) {
      filterOwner = filtersContext.filterObj[MSFilterTypes.Owner];
    }

    if (!isSessionUserObj && props.sessionUser) {
      filterObj[MSFilterTypes.Owner] = filterOwner
        ? [...filterOwner, props.sessionUser.uuid]
        : [props.sessionUser.uuid];
    } else if (filterOwner && props.sessionUser) {
      filterOwner.splice(filterOwner.indexOf(props.sessionUser.uuid), 1);
      filterObj[MSFilterTypes.Owner] = filterOwner;
    }

    void filtersContext.filterObjectives(filterObj);

    setSessionUserObj((state) => !state);
  };

  const applyMultiselectFilter = <T extends MSFilterTypes>(filterName: T, ids: MSFilterPropsI[T]) => {
    const filterObj: Pick<ObjectivesQueryI, 'page' | MSFilterTypes> = {
      page: 1,
      [filterName]: [],
    };

    if (filterName === MSFilterTypes.Owner) {
      filterObj[MSFilterTypes.Owner] = isSessionUserObj ? [props.sessionUser!.uuid] : [];
      if (ids && ids.length) {
        filterObj[MSFilterTypes.Owner] = (filterObj[MSFilterTypes.Owner] || []).concat(ids || []);
      }
    } else {
      filterObj[filterName] = ids && ids.length ? ids : [];
    }

    void filtersContext.filterObjectives(filterObj);
  };

  const applyDueDateFilter = (dates: RangeValue<Moment>) => {
    const filterObj: Pick<ObjectivesQueryI, 'page' | SSFilterTypes.DueDateFrom | SSFilterTypes.DueDateTo> = {
      page: 1,
      [SSFilterTypes.DueDateFrom]: undefined,
      [SSFilterTypes.DueDateTo]: undefined,
    };

    if (dates && dates[0] && dates[1]) {
      filterObj[SSFilterTypes.DueDateFrom] = dates[0].toISOString();
      filterObj[SSFilterTypes.DueDateTo] = dates[1].toISOString();
    }

    void filtersContext.filterObjectives(filterObj);
  };

  const applyProgressFilter = (range?: [number, number]) => {
    const filterObj: Pick<ObjectivesQueryI, 'page' | SSFilterTypes.ProgressFrom | SSFilterTypes.ProgressTo> =
      {
        page: 1,
        [SSFilterTypes.ProgressFrom]: undefined,
        [SSFilterTypes.ProgressTo]: undefined,
      };

    if (range && range[1]) {
      filterObj[SSFilterTypes.ProgressFrom] = range[0];
      filterObj[SSFilterTypes.ProgressTo] = range[1];
    }
    void filtersContext.filterObjectives(filterObj);
  };

  const resetFilters = () => {
    void filtersContext.filterObjectives();
  };

  const toggleRelation = (relationType: RelationTypes) => {
    const relationsFilter = filtersContext.filterObj[MSFilterTypes.HasRelations] || [];
    const relationsFilterIdx = relationsFilter.indexOf(relationType);
    if (relationsFilterIdx === -1) {
      relationsFilter.push(relationType);
    } else {
      relationsFilter.splice(relationsFilterIdx, 1);
    }
    filtersContext.filterObj[MSFilterTypes.HasRelations] = relationsFilter;
    filtersContext.filterObj.page = 1;
    void filtersContext.filterObjectives(filtersContext.filterObj);
  };

  const toggleSubObjectiveRelation = () => {
    toggleRelation(RelationTypes.SubObjectives);
  };

  const someFiltersAreSet = !Object.values(filtersContext.filterObj).every(
    (v) => v === undefined || (Array.isArray(v) && v.length === 0),
  );

  return (
    <FiltersContainer>
      <CoreFiltersBlock>
        <SearchWrapper>
          <SimpleSearchInput
            placeholder={{
              key: 'x.okr.objectives.filterby_name_label',
              value: t('x.okr.objectives.filterby_name_label'),
            }}
            onChange={getObjectivesByName}
            initValue={filtersContext.filterObj && filtersContext.filterObj[SSFilterTypes.Name]}
          />
        </SearchWrapper>
        <MyObjectivesBtn $isSessionUserObj={isSessionUserObj} onClick={toggleSessionUserObjectives}>
          {props.sessionUser && (
            <AvatarBox
              name={getUserFullName(props.sessionUser)}
              url={props.sessionUser.imageUrl}
              size={25}
              type={AvatarType.User}
            />
          )}
          <FilterBtnTxt>{t('x.okr.objectives.my_objectives_btn')}</FilterBtnTxt>
        </MyObjectivesBtn>
      </CoreFiltersBlock>
      <FiltersSetContainer>
        <CollapsibleContainer $timeout={500}>
          <SelectorCol>
            <OwnersSelector
              initialOpts={filtersContext.filterObj[MSFilterTypes.Owner]}
              applyFilter={applyMultiselectFilter}
              key={(filtersContext.filterObj[MSFilterTypes.Owner] || []).join('-')}
            />
          </SelectorCol>
          <SelectorCol>
            <div className={dueDatesInitFilter && dueDatesInitFilter[1] ? '' : 'inactive'}>
              <DateRangeSelector
                value={dueDatesInitFilter}
                onChange={applyDueDateFilter}
                format="DD MMM, YYYY"
                key={`${filtersContext.filterObj[SSFilterTypes.DueDateFrom]}-${
                  filtersContext.filterObj[SSFilterTypes.DueDateTo]
                }`}
              />
            </div>
          </SelectorCol>
          <SelectorCol>
            <StatusSelector
              initialOpts={filtersContext.filterObj[MSFilterTypes.Status]}
              applyFilter={applyMultiselectFilter}
              key={(filtersContext.filterObj[MSFilterTypes.Status] || []).join('-')}
            />
          </SelectorCol>
          <SelectorCol>
            <RangeSelector
              value={progressRangeInitFilter}
              applyRange={applyProgressFilter}
              inactiveKey={t('x.okr.objectives.header.progress_col')}
              key={`${filtersContext.filterObj[SSFilterTypes.ProgressFrom]}-${
                filtersContext.filterObj[SSFilterTypes.ProgressTo]
              }`}
            />
          </SelectorCol>
          <SelectorCol>
            <LabelsSelector
              initialOpts={filtersContext.filterObj[MSFilterTypes.Label]}
              applyFilter={applyMultiselectFilter}
              key={(filtersContext.filterObj[MSFilterTypes.Label] || []).join('-')}
            />
          </SelectorCol>
          <SelectorCol>
            <ToggleButton $state={isSubObjectiveRelationsFilter} onClick={toggleSubObjectiveRelation}>
              <FilterBtnTxt>{t('x.okr.objectives.has_relations_sub_objective')}</FilterBtnTxt>
            </ToggleButton>
          </SelectorCol>
        </CollapsibleContainer>
        {someFiltersAreSet && (
          <ResetFiltersBtn onClick={resetFilters}>
            <SvgIcon name="iconFilter" />
            <span>{t('x.okr.objectives.clear-filters')}</span>
          </ResetFiltersBtn>
        )}
      </FiltersSetContainer>
    </FiltersContainer>
  );
};

const mapState2Props = ({ user }: StoreI): StateProps => ({
  sessionUser: user.session.user,
});

export default connect(mapState2Props)(FiltersSection);
